import SideModal from "components/SideModal";
import { useAuth } from "contexts/AuthContext";
import { useTranslation } from "contexts/i18nContext";
import { useToast } from "contexts/ToastContext";
import { ChangeEvent, useRef, useState } from "react";
import {
  addAccusedSummon,
  editComplaint,
} from "services/prosecutorDecipilineService";
import { Complaints } from "types";
import { LoadingIcon } from "utils/constant";
import { ACCUSED_COMPLAINT_STATUS } from "utils/constantValues";
import "../styles/AddVictimModal.css";

interface ModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  complaintData: Complaints;
  onComplaintRefresh: any;
  isAppealToGeneralCouncil?: boolean;
}

export default function AddAccusedSummonModal({
  isOpen,
  setIsOpen,
  complaintData,
  onComplaintRefresh,
  isAppealToGeneralCouncil = false,
}: ModalProps) {
  const authData = useAuth();
  const { t } = useTranslation();

  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileClaimInputRef = useRef<HTMLInputElement>(null);

  const [formState, setFormState] = useState({
    hearing_date: "",
    region: "",
    zone: "",
    woreda: "",
    kebele: "",
    summary: "",
    status: ACCUSED_COMPLAINT_STATUS.FORWARDED_TO_COORDINATOR,
    claim_summary: "",
    files: [] as File[],
    claimFiles: [] as File[],
  });

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormState((prev) => ({
        ...prev,
        files: [...Array.from(e.target.files as FileList)],
      }));
    }
  };
  const handleClaimFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormState((prev) => ({
        ...prev,
        claimFiles: [...Array.from(e.target.files as FileList)],
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!complaintData?.uu_id) {
      showToast(t("complaintNotFound"), "error");
      return;
    }
    setLoading(true);

    const data = new FormData();
    data.append("hearing_date", formState?.hearing_date ?? "");
    data.append("region", formState?.region ?? "");
    data.append("zone", formState?.zone ?? "");
    data.append("woreda", formState?.woreda ?? "");
    data.append("kebele", formState?.kebele ?? "");
    data.append("claim_summary", formState?.claim_summary ?? "");
    data.append("summon_files", (formState?.files ?? [])[0] || "");
    data.append("claim_summary_files", (formState?.claimFiles ?? [])[0] || "");
    data.append("status", formState.status ?? "");
    data.append(
      "accused_prosecutor_name",
      `${complaintData?.accused_prosecutor_name || ""}`,
    );
    data.append(
      "user_id_accused_prosecutor_name",
      complaintData?.user_id_accused_prosecutor_name || "",
    );
    data.append(
      "assigned_prosecutor",
      complaintData?.assigned_prosecutor || "",
    );
    data.append(
      "user_id_assigned_prosecutor",
      complaintData?.user_id_assigned_prosecutor || "",
    );
    data.append("admin_unit_id", authData?.user?.admin_unit || "");
    data.append("org_id", complaintData?.org_id || "");
    data.append("discipline_complain", String(complaintData?.uu_id ?? ""));
    data.append(
      "is_appeal_to_general_council",
      isAppealToGeneralCouncil ? "true" : "false",
    );

    const isFormData = true;

    try {
      const resData = await addAccusedSummon(data, isFormData);

      if (resData) {
        await editComplaint({
          ...complaintData,
          has_accused: true,
          admin_unit_id: authData?.user?.admin_unit || "",
        });
      }

      onComplaintRefresh();
      setIsOpen(false);
      showToast(t("successfullyCreated"), "success");
      setFormState({
        hearing_date: "",
        region: "",
        zone: "",
        woreda: "",
        kebele: "",
        summary: "",
        claim_summary: "",
        status: ACCUSED_COMPLAINT_STATUS.FORWARDED_TO_COORDINATOR,
        files: [] as File[],
        claimFiles: [] as File[],
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };
  const triggerClaimFileInput = () => {
    fileClaimInputRef.current?.click();
  };

  if (!isOpen) return null;

  return (
    <SideModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={t("createAccusedSummon")}
    >
      <form onSubmit={handleSubmit} className="victim-form">
        <section className="victim-section">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("hearingDate")}
            </label>
            <input
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              type={"date"}
              name="hearing_date"
              placeholder={t("hearingDate")}
              value={formState.hearing_date}
              onChange={handleChange}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("region")}
              </label>
              <input
                required
                className="w-full border bg-gray-100 rounded-md p-2 text-sm"
                type="text"
                name="region"
                value={formState.region}
                onChange={handleChange}
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("zone")}
              </label>
              <input
                required
                className="w-full border bg-gray-100 rounded-md p-2 text-sm"
                type="text"
                name="zone"
                value={formState.zone}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("woreda")}
              </label>
              <input
                required
                className="w-full border bg-gray-100 rounded-md p-2 text-sm"
                type="text"
                name="woreda"
                value={formState.woreda}
                onChange={handleChange}
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("kebele")}
              </label>
              <input
                required
                className="w-full border bg-gray-100 rounded-md p-2 text-sm"
                type="text"
                name="kebele"
                value={formState.kebele}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("claimSummary")}
            </label>
            <textarea
              required
              rows={3}
              name="claim_summary"
              value={formState.claim_summary}
              onChange={handleChange}
              placeholder={t("detailsHere")}
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
            />
          </div>
        </section>

        <section className="evidence-section">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("claimSummaryFile")}
          </label>
          <div className="file-upload-area" onClick={triggerClaimFileInput}>
            <input
              type="file"
              ref={fileClaimInputRef}
              onChange={handleClaimFileChange}
              // multiple
              style={{ display: "none" }}
            />
            <p className="upload-text">{t("uploadFiles")}</p>
            {formState?.claimFiles && formState?.claimFiles?.length > 0 && (
              <div className="file-list">
                {formState?.claimFiles?.map((file, index) => (
                  <div key={index} className="file-item">
                    {file.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="evidence-section">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("summonFile")}
          </label>
          <div className="file-upload-area" onClick={triggerFileInput}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              // multiple
              style={{ display: "none" }}
            />
            <p className="upload-text">{t("uploadFiles")}</p>
            {formState?.files && formState?.files?.length > 0 && (
              <div className="file-list">
                {formState?.files?.map((file, index) => (
                  <div key={index} className="file-item">
                    {file.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <button
          type="submit"
          className="bg-black w-full text-white px-6 py-2 rounded-md hover:bg-gray-800"
          disabled={loading}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              {LoadingIcon}
              {t("submitting")}
            </div>
          ) : (
            t("addAccusedSummon")
          )}
        </button>
      </form>
    </SideModal>
  );
}
